import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { format } from "date-fns";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { de } from 'date-fns/locale';

import kidsApi from "@api/kids.api";

import MainContentHeader from "@components/MainContentHeader";
import stores from "@stores";
import * as commonS from "@styles/common";

import useKidsImport from "./useKidsImport";
import useCurrentProject from "@hooks/useCurrentProject";

import Table from "@components/Table";
import Modal from "@components/Modal";
import MoreActionsButton from "@components/Table/MoreActionsButton";
import { Button, Icon } from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import { getGender, getGermanLanguageLevel } from "@helpers/getGermanNaming";

import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-google-places-autocomplete";
import * as S from "./styles";
import KidHint from "@components/Hints/KidHint";
import classesApi from "@api/classes.api";

const KidsView = ({ project }) => {
  const {
    studentsStore,
    layoutStore: { setConfirmModal },
  } = stores;
  const { kids } = studentsStore;
  const [emptyValuesArr, setEmptyValuesArr] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [address, setAddress] = useState(null);
  const [kid, setKid] = useState({});
  const [currentKid, setCurrentKid] = useState(null);
  const [teachers, setTeachers] = useState(new Set());
  const [isEdit, setIsEdit] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { currentProject } = useCurrentProject();

  const isEmptyValue = (obj, key) => {
    if (!obj[key] || obj[key] === " ") return true;
    return false;
  };

  const errorArr = kids
    .filter(
      (elem) =>
        isEmptyValue(elem, "fullName") ||
        isEmptyValue(elem, "germanLanguageLevel") ||
        isEmptyValue(elem, "address")
    )
    .map((item) => item._id);

  const onImportCompleted = async () => {
    await studentsStore.fetchKidsList(project._id);
  };

  const { openImport, closeImport, isImportActive, ImportView } = useKidsImport(
    {
      projectId: project._id,
      onImportCompleted,
    }
  );

  useEffect(() => {
    const getKidsList = async () => {
      await studentsStore.fetchKidsList(project._id);
      const classes = await classesApi.fetch(project._id);
      const classTeachers = new Set();
      classes.forEach((e) => classTeachers.add(e.teacherName));
      setTeachers(classTeachers);
    };
    getKidsList();
  }, []);

  useEffect(() => {
    if (address) {
      geocodeByAddress(address?.label)
        .then((results) => getLatLng(results[0]))
        .then(({ lat, lng }) =>
          setKid({
            ...kid,
            address: address?.label,
            location: {
              type: "Point",
              coordinates: [lng, lat],
            },
            placeId: address?.value?.place_id,
          })
        );
    }
  }, [address?.label]);

  const onDeleteKid = (id: string) => {
    setConfirmModal({
      type: "warning",
      open: true,
      title: "Element wirklich löschen?",
      positiveCallback: async () => {
        await kidsApi.delete(id).then((e: any) =>
          toast.success("SuS erfolgreich gelöscht’", { autoClose: 3000 })
        );
        await studentsStore.fetchKidsList(project._id);
      },
      positiveButtonText: "Ok",
    });
  };

  const onDeleteAll = async () => {
    setConfirmModal({
      type: "warning",
      open: true,
      title: "Are you sure you want to remove all items ?",
      positiveCallback: async () => {
        for (const kid of kids) {
          await kidsApi.delete(kid._id);
          await studentsStore.fetchKidsList(project._id);
        }
      },
      positiveButtonText: "Ok",
    });
  };

  if (!kids) {
    return <div>Loading....</div>;
  }

  const headCells = [
    { title: "#", key: "number", sortable: false, width: "4%" },
    { title: "Name", key: "fullName", sortable: false, width: "135px" },
    { title: "Adresse", key: "address", sortable: false, width: "200px" },
    { title: "Geschlecht", key: "gender", sortable: false },
    { title: "Geburtstag", key: "birthday", sortable: false, width: "130px" },
    {
      title: "Lehrperson",
      key: "teacherName",
      sortable: false,
      width: "135px",
    },
    {
      title: "Erfahrung Betreuung",
      key: "previousExperience",
      sortable: false,
    },
    { title: "Deutsch", key: "germanLanguageLevel", sortable: false },
    { title: "Umgangssprache", key: "motherLanguage", sortable: false },
    { title: "Aktionen", key: "actions", sortable: false },
  ];

  const handleOpenEditModal = (row: any) => {
    setIsEdit(true);
    setModalOpen(true);
    setCurrentKid(row);
  };

  const handleEdit = async () => {
    setEmptyValuesArr(errorArr);
    await kidsApi
      .edit({
        ...currentKid,
        fullName: `${currentKid?.lastName} ${currentKid?.firstName}`,
        birthday: currentKid?.birthday
          ? format(new Date(currentKid?.birthday), "dd. MMMM yyyy")
          : "",
        id: currentKid._id,
      })
      .then((e: any) =>
        toast.success("Kid was edited successfully", { autoClose: 3000 })
      );
    await studentsStore.fetchKidsList(project._id);
    setModalOpen(false);
    setIsEdit(false);
  };

  useEffect(() => {
    if (kids.length) {
      setEmptyValuesArr(errorArr);
    }
  }, [kids]);

  const handleEditAddress = (e: any, row) => {
    setEmptyValuesArr(errorArr);
    geocodeByAddress(e?.label)
      .then((results) => getLatLng(results[0]))
      .then(({ lat, lng }) =>
        setCurrentKid({
          ...currentKid,
          address: e.label,
          placeId: e.value.place_id,
          location: {
            type: "Point",
            coordinates: [lng, lat],
          },
        })
      );
  };

  const handleChange = ({ target }: { target: HTMLInputElement }) => {
    const { name, value, checked } = target;
    if (isEdit) {
      if (name === "previousExperience" || name === "schoolBus") {
        setCurrentKid({
          ...currentKid,
          [name]: checked,
        });
      } else {
        setCurrentKid({
          ...currentKid,
          [name]: value,
        });
      }
    } else {
      if (name === "previousExperience" || name === "schoolBus") {
        setKid({
          ...kid,
          [name]: checked,
        });
      } else {
        setKid({
          ...kid,
          [name]: value,
        });
      }
    }
  };

  useEffect(() => {
    if (
      kid?.firstName &&
      kid?.lastName &&
      kid?.address &&
      kid?.gender &&
      kid?.germanLanguageLevel
    ) {
      setButtonDisabled(false);
    }
  }, [kid]);

  const handleAddNew = async () => {
    setModalOpen(false);
    await kidsApi
      .add({
        ...kid,
        birthday: kid?.birthday
          ? format(new Date(kid?.birthday), "dd. MMMM yyyy")
          : "",
        fullName: `${kid?.lastName} ${kid?.firstName}`,
        project: currentProject._id,
      })
      .then((e: any) =>
        toast.success('SuS erfolgreich hinzugefügt', { autoClose: 3000 })
      );
    await setKid({});
    await setAddress(null);
    await studentsStore.fetchKidsList(project._id);
    setButtonDisabled(true);
  };

  const getFormattedRows = () =>
    kids.length === 0
      ? []
      : kids
          .sort((a, b) => {
            if (a.lastName && b.lastName) {
              return a.lastName.localeCompare(b.lastName);
            } else {
              return a.fullName.localeCompare(b.fullName);
            }
          })
          .map((row: any, index: number) => {
            return {
              id: row?._id,
              number: index + 1,
              fullName: (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <S.TableLabel
                    className={isEmptyValue(row, "fullName") && "error"}
                  >
                    {isEmptyValue(row, "fullName") ? (
                      <span onClick={() => handleOpenEditModal(row)}>
                        Please edit and enter full name
                      </span>
                    ) : (
                      <strong onClick={() => handleOpenEditModal(row)}>
                        {row?.lastName} {row?.firstName}
                      </strong>
                    )}
                  </S.TableLabel>
                  {row?.note && (
                    <svg
                      width="16"
                      height="16"
                      style={{ marginLeft: 5 }}
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.69618 8.03551L4 8.56414L4.52857 5.86791L9.9476 0.448923C10.5462 -0.149642 11.5166 -0.149641 12.1152 0.448924C12.7138 1.04749 12.7138 2.01795 12.1152 2.61652L6.69618 8.03551Z"
                        fill="#778097"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M1.25 2.25H5.74609C6.09127 2.25 6.37109 1.97018 6.37109 1.625C6.37109 1.27982 6.09127 1 5.74609 1H1.09375C0.803669 1 0.52547 1.11523 0.320352 1.32035C0.115234 1.52547 0 1.80367 0 2.09375V11.4688C0 11.7588 0.115234 12.037 0.320352 12.2421C0.525471 12.4473 0.803671 12.5625 1.09375 12.5625H10.4688C10.7588 12.5625 11.037 12.4473 11.2421 12.2421C11.4473 12.037 11.5625 11.7588 11.5625 11.4688V6.81641C11.5625 6.47123 11.2827 6.19141 10.9375 6.19141C10.5923 6.19141 10.3125 6.47123 10.3125 6.81641V11.3125H1.25V2.25Z"
                        fill="#778097"
                      />
                    </svg>
                  )}
                  {row?.schoolBus && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: 3 }}
                      height="16"
                      viewBox="0 0 24 24"
                      width="16"
                    >
                      <path d="M0 0h24v24H0z" fill="none" />
                      <path
                        fill="#778097"
                        d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"
                      />
                    </svg>
                  )}
                </div>
              ),
              address: (
                <S.TableLabel
                  className={isEmptyValue(row, "address") && "error"}
                  onClick={() => handleOpenEditModal(row)}
                >
                  {isEmptyValue(row, "address")
                    ? "Please edit and enter address"
                    : row?.address}
                </S.TableLabel>
              ),
              gender: (
                <S.TableLabel
                  className={isEmptyValue(row, "gender") && "error"}
                  onClick={() => handleOpenEditModal(row)}
                >
                  {isEmptyValue(row, "gender")
                    ? "Please edit and enter gender"
                    : getGender(row?.gender)}
                </S.TableLabel>
              ),
              teacherName: (
                <S.TableLabel
                  className={
                    !!teachers.size && !teachers.has(row.teacherName)
                      ? "error"
                      : ""
                  }
                  onClick={() => handleOpenEditModal(row)}
                >
                  {row?.teacherName}
                </S.TableLabel>
              ),
              previousExperience: row?.previousExperience ? "Ja" : "nein",
              motherLanguage: row?.motherLanguage,
              germanLanguageLevel: getGermanLanguageLevel(
                row?.germanLanguageLevel
              ),
              birthday: row.birthday ? format(new Date(row.birthday), "dd. MMMM yyyy", { locale: de }) : ''              ,
              actions: (
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  <MoreActionsButton
                    onDelete={() => onDeleteKid(row._id, row.fullName)}
                    isEdit
                    onEdit={() => handleOpenEditModal(row)}
                  />
                  <S.InfoWrap
                    onClick={(event) => {
                      setAnchorEl(event.currentTarget);
                      setCurrentKid(row);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="#4F77DE"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-.001 5.75c.69 0 1.251.56 1.251 1.25s-.561 1.25-1.251 1.25-1.249-.56-1.249-1.25.559-1.25 1.249-1.25zm2.001 12.25h-4v-1c.484-.179 1-.201 1-.735v-4.467c0-.534-.516-.618-1-.797v-1h3v6.265c0 .535.517.558 1 .735v.999z" />
                    </svg>
                  </S.InfoWrap>
                </div>
              ),
            };
          });

  return (
    <>
      <commonS.MainBody>
        <MainContentHeader
          title={project.title}
          buttonTitle={isImportActive ? "Import abbrechen" : "Importieren"}
          onButtonClick={isImportActive ? closeImport : openImport}
        />
        <div style={{ marginBottom: isImportActive ? 20 : 0 }}>
          {ImportView}
        </div>

        {kids?.length === 0 ? (
          <h2>Es wurden keine SuS hinzugefügt oder importiert</h2>
        ) : (
          <>
            <Table
              headRows={headCells}
              bodyRows={getFormattedRows()}
              errorRowIds={emptyValuesArr}
              isSus
              sorting={{
                sort: "",
                onChangeSorting: () => alert("SortChange"),
              }}
            />
            <S.TableFooter>
              <div>
                <Button variant="text" onClick={() => setModalOpen(true)}>
                  + Neue Hinzufügen
                </Button>
              </div>
              <div style={{ marginLeft: "auto" }}>
                <Button variant="text" onClick={onDeleteAll}>
                  Alles löschen
                </Button>
              </div>
            </S.TableFooter>

            <Modal
              modalOpen={isModalOpen}
              isEdit={isEdit}
              onClose={() => {
                setModalOpen(false);
                setIsEdit(false);
              }}
              disabled={isEdit ? false : isButtonDisabled}
              onConfirm={isEdit ? handleEdit : handleAddNew}
              setModalOpen={setModalOpen}
              title={isEdit ? "SuS bearbeiten" : "Neue Hinzufügen"}
            >
              <S.FormItem>
                <InputLabel htmlFor="firstName" shrink={false}>
                  Vorname*
                </InputLabel>
                <TextField
                  value={(isEdit && currentKid.firstName) || kid.firstName}
                  onChange={handleChange}
                  variant="outlined"
                  name="firstName"
                  id="firstName"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="lastName" shrink={false}>
                  Name*
                </InputLabel>
                <TextField
                  value={(isEdit && currentKid.lastName) || kid.lastName}
                  onChange={handleChange}
                  variant="outlined"
                  name="lastName"
                  id="lastName"
                />
              </S.FormItem>

              {isEdit ? (
                <S.FormItem>
                  <InputLabel htmlFor="address" shrink={false}>
                    Adresse*
                  </InputLabel>
                  <GooglePlacesAutocomplete
                    apiOptions={{ language: "de", region: "ch" }}
                    autocompletionRequest={{
                      componentRestrictions: {
                        country: ["ch"],
                      },
                    }}
                    selectProps={{
                      value: !isEmptyValue(currentKid, "address")
                        ? {
                            label: currentKid?.address,
                            value: {
                              place_id: currentKid?.placeId,
                            },
                          }
                        : {
                            label: "Plese enter address",
                            value: {
                              place_id: null,
                            },
                          },
                      onChange: (e) => handleEditAddress(e, currentKid),
                      styles: {
                        input: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                        control: (provided: any, state: any) => ({
                          ...provided,
                          borderColor: state.isFocused
                            ? "#4F77DE"
                            : "rgba(102, 102, 102, 0.35)",
                          outline: "none",
                          boxShadow: "none",
                          borderRadius: 8,
                          padding: "2.5px 5px",
                        }),
                        option: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                        singleValue: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                      },
                    }}
                  />
                </S.FormItem>
              ) : (
                <S.FormItem>
                  <InputLabel htmlFor="address" shrink={false}>
                    Adresse*
                  </InputLabel>
                  <GooglePlacesAutocomplete
                    apiOptions={{ language: "de", region: "ch" }}
                    autocompletionRequest={{
                      componentRestrictions: {
                        country: ["ch"],
                      },
                    }}
                    selectProps={{
                      value: address,
                      onChange: setAddress,
                      styles: {
                        input: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                        control: (provided: any, state: any) => ({
                          ...provided,
                          borderColor: state.isFocused
                            ? "#4F77DE"
                            : "rgba(102, 102, 102, 0.35)",
                          outline: "none",
                          boxShadow: "none",
                          borderRadius: 8,
                          padding: "2.5px 5px",
                        }),
                        option: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                        singleValue: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                      },
                    }}
                  />
                </S.FormItem>
              )}

              <S.FormRow>
                <S.FormItem>
                  <InputLabel htmlFor="genderSelect" shrink={false}>
                    Geschlecht*
                  </InputLabel>
                  <Select
                    labelId="genderSelect"
                    id="gender"
                    label="genderSelect"
                    name="gender"
                    value={(isEdit && currentKid.gender) || kid.gender}
                    onChange={handleChange}
                  >
                    <MenuItem value="male">Männlich</MenuItem>
                    <MenuItem value="female">Weiblich</MenuItem>
                  </Select>
                </S.FormItem>

                <S.FormItem>
                  <InputLabel htmlFor="birthdayLabel" shrink={false}>
                    Geburtstag
                  </InputLabel>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                    <DesktopDatePicker
                      value={(isEdit && currentKid.birthday) || kid?.birthday}
                      inputFormat="dd. MMMM yyyy"
                      disableFuture
                      name="birthday"
                      inputProps={{
                        disabled: true,
                        readOnly: true,
                        name: "birthday",
                      }}
                      onChange={(e) => {
                        if (isEdit) {
                          setCurrentKid({ ...currentKid, birthday: e });
                        } else {
                          setKid({ ...kid, birthday: e });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField name="birthday" disabled {...params} />
                      )}
                    />
                  </LocalizationProvider>
                </S.FormItem>
              </S.FormRow>
              <S.FormRow>
                <S.FormItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="previousExperience"
                        onChange={handleChange}
                        checked={
                          (isEdit && currentKid.previousExperience) ||
                          kid?.previousExperience
                        }
                      />
                    }
                    label="Erfahrung Betreuung"
                  />
                </S.FormItem>

                <S.FormItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="schoolBus"
                        onChange={handleChange}
                        checked={
                          (isEdit && currentKid.schoolBus) || kid.schoolBus
                        }
                      />
                    }
                    label="Schulbus"
                  />
                </S.FormItem>
              </S.FormRow>
              <S.FormRow>
                <S.FormItem>
                  <InputLabel htmlFor="susId" shrink={false}>
                    SuS_ID
                  </InputLabel>
                  <TextField
                    id="susId"
                    onChange={handleChange}
                    value={(isEdit && currentKid.susId) || kid.susId}
                    name="susId"
                    variant="outlined"
                  />
                </S.FormItem>
                <S.FormItem>
                  <InputLabel htmlFor="teacherName" shrink={false}>
                    Lehrperson
                  </InputLabel>
                  <Select
                    labelId="teacherName"
                    id="teacherName"
                    label="teacherName"
                    name="teacherName"
                    value={
                      (isEdit && currentKid.teacherName) || kid.teacherName
                    }
                    onChange={handleChange}
                  >
                    <MenuItem value=" ">Nein</MenuItem>
                    {teachers.size &&
                      [...teachers].map((e) => (
                        <MenuItem value={e}>{e}</MenuItem>
                      ))}
                  </Select>
                </S.FormItem>
              </S.FormRow>
              <S.FormRow>
                <S.FormItem>
                  <InputLabel htmlFor="germanLanguageLevel" shrink={false}>
                    Deutsch*
                  </InputLabel>
                  <Select
                    labelId="germanLanguageLevel"
                    id="germanLanguageLevel"
                    label="germanLanguageLevel"
                    name="germanLanguageLevel"
                    value={
                      (isEdit && currentKid.germanLanguageLevel) ||
                      kid.germanLanguageLevel
                    }
                    onChange={handleChange}
                  >
                    <MenuItem value="none">Gar kein</MenuItem>
                    <MenuItem value="little">Wenig</MenuItem>
                    <MenuItem value="medium">Mittel</MenuItem>
                    <MenuItem value="high">Viel</MenuItem>
                    <MenuItem value="fluent">Ausschliesslich</MenuItem>
                  </Select>
                </S.FormItem>
                <S.FormItem>
                  <InputLabel htmlFor="motherLanguage" shrink={false}>
                    Umgangssprache
                  </InputLabel>
                  <TextField
                    id="motherLanguage"
                    onChange={handleChange}
                    value={
                      (isEdit && currentKid.motherLanguage) ||
                      kid.motherLanguage
                    }
                    name="motherLanguage"
                    variant="outlined"
                  />
                </S.FormItem>
              </S.FormRow>

              <S.FormItem>
                <InputLabel htmlFor="note" shrink={false}>
                  Notizen
                </InputLabel>
                <TextField
                  id="note"
                  onChange={handleChange}
                  value={(isEdit && currentKid.note) || kid.note}
                  name="note"
                  variant="outlined"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="specialNote" shrink={false}>
                  Wunsch
                </InputLabel>
                <TextField
                  id="specialNote"
                  onChange={handleChange}
                  value={(isEdit && currentKid.specialNote) || kid.specialNote}
                  name="specialNote"
                  variant="outlined"
                />
              </S.FormItem>
            </Modal>

            {!!currentKid && (
              <KidHint
                kid={currentKid}
                handleClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
              />
            )}
          </>
        )}
      </commonS.MainBody>

      <Helmet>
        <title>Klassen-Einteilung SuS</title>
      </Helmet>
    </>
  );
};

export default observer(KidsView);
